import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { mockQuotes } from '../mock';
import { LogOut, Mail, Phone, Calendar, Package, TrendingUp } from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    // Check authentication
    const isAuth = localStorage.getItem('isAdminAuthenticated');
    if (!isAuth) {
      navigate('/admin');
    }
    
    // Load mock quotes
    setQuotes(mockQuotes);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('isAdminAuthenticated');
    navigate('/admin');
  };

  const updateQuoteStatus = (id, newStatus) => {
    setQuotes(quotes.map(q => q.id === id ? {...q, status: newStatus} : q));
  };

  const filteredQuotes = filter === 'all' ? quotes : quotes.filter(q => q.status === filter);

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-500 text-white',
      contacted: 'bg-yellow-500 text-white',
      completed: 'bg-green-500 text-white'
    };
    return colors[status] || 'bg-gray-500 text-white';
  };

  const stats = {
    total: quotes.length,
    new: quotes.filter(q => q.status === 'new').length,
    contacted: quotes.filter(q => q.status === 'contacted').length,
    completed: quotes.filter(q => q.status === 'completed').length
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="https://customer-assets.emergentagent.com/job_cabinet-hero/artifacts/3942icok_image.png" alt="Cabinet Hero" className="h-12 w-12" />
              <div>
                <h1 className="text-xl font-bold text-[#1e3a8a]">Cabinet Hero</h1>
                <p className="text-sm text-gray-600">Admin Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/')}>
                View Website
              </Button>
              <Button variant="ghost" onClick={handleLogout} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="border-2">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Total Quotes</p>
                  <p className="text-3xl font-bold text-[#1e3a8a] mt-2">{stats.total}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#1e3a8a]/10 flex items-center justify-center">
                  <Package className="w-6 h-6 text-[#1e3a8a]" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-2">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">New Requests</p>
                  <p className="text-3xl font-bold text-blue-600 mt-2">{stats.new}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-2">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">In Progress</p>
                  <p className="text-3xl font-bold text-yellow-600 mt-2">{stats.contacted}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-2">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Completed</p>
                  <p className="text-3xl font-bold text-green-600 mt-2">{stats.completed}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Package className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quotes List */}
        <Card className="border-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl text-[#1e3a8a]">Quote Requests</CardTitle>
                <CardDescription>Manage and track customer inquiries</CardDescription>
              </div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Quotes</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filteredQuotes.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No quotes found</p>
                </div>
              ) : (
                filteredQuotes.map((quote) => (
                  <div key={quote.id} className="border-2 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-[#1e3a8a]">{quote.name}</h3>
                        <Badge className={`${getStatusColor(quote.status)} mt-2`}>
                          {quote.status === 'new' ? 'New Request' : quote.status === 'contacted' ? 'In Progress' : 'Completed'}
                        </Badge>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          {new Date(quote.date).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="w-4 h-4" />
                        <span>{quote.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone className="w-4 h-4" />
                        <span>{quote.phone}</span>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                      <div className="grid grid-cols-2 gap-4 mb-2">
                        <div>
                          <p className="text-sm text-gray-600 font-medium">Service</p>
                          <p className="text-[#1e3a8a] font-semibold">{quote.service}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 font-medium">Project Size</p>
                          <p className="text-[#1e3a8a] font-semibold">{quote.projectSize}</p>
                        </div>
                      </div>
                      {quote.message && (
                        <div>
                          <p className="text-sm text-gray-600 font-medium mb-1">Message</p>
                          <p className="text-gray-700">{quote.message}</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <Select 
                        value={quote.status} 
                        onValueChange={(value) => updateQuoteStatus(quote.id, value)}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">Mark as New</SelectItem>
                          <SelectItem value="contacted">Mark as In Progress</SelectItem>
                          <SelectItem value="completed">Mark as Completed</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="outline" className="border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white">
                        Contact Customer
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;